import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSupportStore from '@/stores/Common/SupportStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './CallUs.css';

const CallUs: React.FC = () => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = React.useState(false);

  const {
    contactInfo,
    loading,
    error,
    fetchContactInfo,
  } = useSupportStore();

  // Fetch contact info on mount
  useEffect(() => {
    if (!contactInfo) {
      fetchContactInfo();
    }
  }, [contactInfo, fetchContactInfo]);

  const handleBack = () => {
    setIsNavigating(true);
    navigate(-1);
  };

  if (isNavigating) {
    return (
      <div className="callus-container">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  if (loading && !contactInfo) {
    return (
      <div className="callus-container">
        <button className="callus-back-button" onClick={handleBack}>
          <span className="callus-back-arrow">←</span>
          <span className="callus-back-text">Back</span>
        </button>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !contactInfo) {
    return (
      <div className="callus-container">
        <button className="callus-back-button" onClick={handleBack}>
          <span className="callus-back-arrow">←</span>
          <span className="callus-back-text">Back</span>
        </button>
        <ErrorDisplay
          title="Failed to Load Contact Information"
          message={error}
          type="error"
          showRetry
          onRetry={fetchContactInfo}
        />
      </div>
    );
  }

  return (
    <div className="callus-container">
      <button className="callus-back-button" onClick={handleBack}>
        <span className="callus-back-arrow">←</span>
        <span className="callus-back-text">Back</span>
      </button>

      <div className="callus-header-card">
        <h1 className="callus-title">Call Us</h1>
        <p className="callus-subtitle">Speak directly with our support team</p>
      </div>

      <div className="callus-hotline-card">
        <div className="callus-hotline-header">
          <div className="callus-icon-circle">
            <svg className="callus-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </div>
          <div>
            <h2 className="callus-hotline-title">Customer Support Hotline</h2>
            <p className="callus-hotline-subtitle">Available 24/7</p>
          </div>
        </div>
        <div className="callus-phone-number">{contactInfo?.mainHotline || '+1 (555) 123-4567'}</div>
      </div>

      <div className="callus-grid-container">
        <div className="callus-card">
          <div className="callus-card-icon-circle">
            <svg className="callus-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div className="callus-card-content">
            <h3 className="callus-card-title">Support Hours</h3>
            <p className="callus-card-text">{contactInfo?.supportHours || '24 hours a day, 7 days a week'}</p>
          </div>
        </div>

        <div className="callus-card">
          <div className="callus-card-icon-circle">
            <svg className="callus-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <div className="callus-card-content">
            <h3 className="callus-card-title">International Support</h3>
            {contactInfo?.internationalNumbers.map((intl, index) => (
              <p key={index} className="callus-card-text">
                {intl.phoneNumber} ({intl.country})
              </p>
            )) || (
              <>
                <p className="callus-card-text">+44 20 1234 5678 (UK)</p>
                <p className="callus-card-text">+61 2 1234 5678 (AU)</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="callus-grid-container">
        <div className="callus-card">
          <div className="callus-card-icon-circle">
            <svg className="callus-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <div className="callus-card-content">
            <h3 className="callus-card-title">Email Support</h3>
            <a href={`mailto:${contactInfo?.email || 'support@example.com'}`} className="callus-email-link">
              {contactInfo?.email || 'support@example.com'}
            </a>
            <p className="callus-card-text-small">{contactInfo?.emailResponseTime || 'Response within 2 hours'}</p>
          </div>
        </div>

        <div className="callus-card">
          <div className="callus-card-icon-circle">
            <svg className="callus-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div className="callus-card-content">
            <h3 className="callus-card-title">Visit Us</h3>
            <p className="callus-card-text">{contactInfo?.address.street || '123 Support Street'}</p>
            <p className="callus-card-text">
              {contactInfo?.address.city || 'San Francisco'}, {contactInfo?.address.state || 'CA'} {contactInfo?.address.zipCode || '94102'}
            </p>
            <p className="callus-card-text">{contactInfo?.address.country || 'United States'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallUs;
